//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import SyncModel from './sync.model';

/**
 * @service SyncService
 * @description Service for synchronizing Access Control List (ACL) configurations between the frontend application and the backend system.
 * This service is responsible for ensuring that the backend is aware of the current route definitions, roles, and their associated permissions
 * as defined in the frontend application structure.
 *
 * It interacts with the `/acl/` backend endpoint.
 *
 * **Key Responsibilities:**
 * 1. **ACL Synchronization**: Pushing local route and role definitions to the server to update the central ACL repository.
 * 2. **Permission Management**: Facilitating the alignment of frontend navigation guards with backend authorization checks.
 * 3. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * **Data Flow:**
 * - **Sync**: The application (usually an admin module or during startup) constructs a `SyncModel` containing routes and roles.
 * - **Transmission**: `syncACLDetails` sends this payload to `/acl/syncACLDetails`.
 * - **Update**: The backend processes this data to update its permission tables.
 *
 * @feature 1. Synchronize ACL details (Routes, Roles).
 * @feature 2. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<SyncModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /acl/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class SyncService extends Service<SyncModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/acl/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/acl/');
  }

  /**
   * @method syncACLDetails
   * @async
   * @description Synchronize Access Control List (ACL) details with the backend.
   *
   * This method sends a payload containing the application's current route definitions and role mappings.
   * It is typically used to initialize or update the backend's authorization database to match the frontend's capabilities.
   *
   * @param {SyncModel} syncData - The model containing the ACL data (routes, roles) to be synced.
   * @returns {Promise<SyncModel>} Promise resolving to the response from the sync operation (usually the synced data or status).
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `syncACLDetails`.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * const aclData = new SyncModel();
   * // populate aclData...
   * syncService.syncACLDetails(aclData).then(response => {
   *   console.log('ACL Synced Successfully');
   * });
   */
  public syncACLDetails(syncData: SyncModel) {
    return new Promise<SyncModel>((resolve, reject) => {
      const request = this.httpClient.post<SyncModel>(`${this.baseUrl}${this.serviceUrl}syncACLDetails`, syncData, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
